import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { onboardingAPI } from '../../services/api';

const parqSchema = z.object({
  heartCondition: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  chestPainActive: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  chestPainResting: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  loseBalance: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  boneOrJoint: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  prescribedDrugs: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  noActivityReason: z.enum(['Yes', 'No'], { required_error: 'Please select an option' })
});

type PARQFormData = z.infer<typeof parqSchema>;

interface PARQFormProps {
  onComplete: () => void;
}

const PARQForm: React.FC<PARQFormProps> = ({ onComplete }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<PARQFormData>({
    resolver: zodResolver(parqSchema)
  });

  const onSubmit = async (data: PARQFormData) => {
    setIsSubmitting(true);
    try {
      await onboardingAPI.submitPARQ(data);
      onComplete();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to submit PARQ form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const RadioGroup = ({ name, question, error }: { name: keyof PARQFormData; question: string; error?: string }) => (
    <div className="mb-6">
      <fieldset>
        <legend className="text-sm font-medium text-gray-900 mb-3">
          **{question}
        </legend>
        <div className="flex space-x-6">
          <label className="flex items-center">
            <input
              {...register(name)}
              type="radio"
              value="Yes"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">Yes</span>
          </label>
          <label className="flex items-center">
            <input
              {...register(name)}
              type="radio"
              value="No"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">No</span>
          </label>
        </div>
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      </fieldset>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white shadow rounded-lg p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">WebFit PAR-Q</h1>
          <p className="text-red-600 font-medium">**FIELDS ARE REQUIRED</p>
          <p className="text-gray-600 mt-2">
            The Physical Activity Readiness Questionnaire (PAR-Q) helps determine if you're ready to begin an exercise program safely.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <RadioGroup
            name="heartCondition"
            question="Has your doctor ever said that you have a heart condition and that you should only do physical activity recommended by a doctor?"
            error={errors.heartCondition?.message}
          />

          <RadioGroup
            name="chestPainActive"
            question="Do you feel pain in your chest when you do physical activity?"
            error={errors.chestPainActive?.message}
          />

          <RadioGroup
            name="chestPainResting"
            question="In the past month, have you had chest pain when you were not doing physical activity?"
            error={errors.chestPainResting?.message}
          />

          <RadioGroup
            name="loseBalance"
            question="Do you lose your balance because of dizziness, or do you ever lose consciousness?"
            error={errors.loseBalance?.message}
          />

          <RadioGroup
            name="boneOrJoint"
            question="Do you have a bone or joint problem (for example, back, knee or hip) that could be made worse by a change in your physical activity?"
            error={errors.boneOrJoint?.message}
          />

          <RadioGroup
            name="prescribedDrugs"
            question="Is your doctor currently prescribing drugs (for example, water pills) for your blood pressure or heart condition?"
            error={errors.prescribedDrugs?.message}
          />

          <RadioGroup
            name="noActivityReason"
            question="Do you know of any other reason why you should not do physical activity?"
            error={errors.noActivityReason?.message}
          />

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Important Note:</h3>
            <p className="text-sm text-blue-700">
              If you answered "Yes" to any of the questions above, please consult with your healthcare provider before starting an exercise program. 
              Your safety is our top priority.
            </p>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Continue >'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PARQForm;
